import React, { useCallback } from 'react';
import { SimulationDateString } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import {
    MantyStatus,
    RecurrenceEnum,
    RetirementSystem,
} from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { OrgChartHr } from 'routes/HumanResourcesModule/PayState/helpers/types';
import {
    FilteredMantyStatus,
    NEW_AGENT,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/helpers/constants';
import { GridEchelons } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/EchelonsOverview/helpers/types';

import AgentArrivalHypothesisBaseModal from './AgentArrivalHypothesisBaseModal';
import { AgentArrivalHypothesisMutationParams } from '../helpers/hooks/agentArrivalHypothesis';
import { useFormState } from '../hooks/agentArrival';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { useEnvelopeMantyStatusHypothesesChangesContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/MantyStatusHypothesis/contexts/EnvelopeMantyStatusHypothesesChangesContext';

type Props = {
    createHypothesis: (payload: AgentArrivalHypothesisMutationParams) => void;
    hrSimulation: HrSimulation;
    officialGrades: GridEchelons[];
    onClose: () => void;
    orgChartHrs: OrgChartHr[];
    recurrence: RecurrenceEnum;
    show: boolean;
};

const AgentArrivalHypothesisCreationModal = ({
    createHypothesis,
    hrSimulation,
    officialGrades,
    onClose,
    orgChartHrs,
    recurrence,
    show,
}: Props): React.ReactElement => {
    const currentDate = new Date()
        .toISOString()
        .slice(0, 10) as SimulationDateString;

    const { nonRecurrentEnvelopeStatuses } =
        useEnvelopeMantyStatusHypothesesChangesContext();
    const nonRecurentList = Object.values(MantyStatus).filter((item) => {
        return (
            !nonRecurrentEnvelopeStatuses.includes(item) &&
            FilteredMantyStatus.includes(item)
        );
    });

    const [formState, setFormState] = useFormState({
        agentETP: 1,
        agentFirstName: '',
        agentLastName: '',
        echelon: '0',
        filiere: '',
        ifse: 0,
        isFormValid: false,
        jobLabel: '',
        jobNumber: '',
        majoredSalary: 0,
        mantyStatus:
            recurrence === RecurrenceEnum.RECURRENT
                ? MantyStatus.TITULAIRE
                : nonRecurentList[0],
        recurrence: recurrence,
        officialGrade: '',
        retirementSystem: RetirementSystem.CNRACL,
        selectedOrgChartHrSoftwareId: orgChartHrs[0]?.softwareId ?? '',
        selectedStartDate: currentDate,
        selectedEndDate: undefined,
    });
    const onValidate = useCallback(() => {
        const creationPayload = {
            agentFirstName: formState.agentFirstName,
            agentLastName:
                formState.agentLastName === ''
                    ? NEW_AGENT
                    : formState.agentLastName,
            startDate: formState.selectedStartDate,
            endDate: formState.selectedEndDate,
            etp: formState.agentETP,
            salary: formState.majoredSalary,
            recurrence: formState.recurrence,
            jobLabel: formState.jobLabel ?? '',
            jobNumber: formState.jobNumber ?? '',
            orgChartHrSoftwareId: formState.selectedOrgChartHrSoftwareId,
            retirementSystem: formState.retirementSystem,
            mantyStatus: formState.mantyStatus,
            filiere: formState.filiere,
            officialGrade: formState.officialGrade,
            echelon: Number(formState.echelon),
            ifse: formState.ifse,
        };
        createHypothesis(creationPayload);
    }, [formState, createHypothesis]);
    const onClickSave = () => {
        onValidate();
    };

    return (
        <>
            <AgentArrivalHypothesisBaseModal
                editMode={false}
                formState={formState}
                hrSimulation={hrSimulation}
                officialGrades={officialGrades}
                onClickSave={onClickSave}
                onClose={onClose}
                orgChartHrs={orgChartHrs}
                setFormState={setFormState}
                show={show}
            />
        </>
    );
};

export default AgentArrivalHypothesisCreationModal;
